import { motion } from "framer-motion";
import { CheckCircle2, Car, Users, Phone, Clock, MapPin, Plane, Bus } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const airportTransfer = [
  { name: "Sedan 4+1", price: "₹3800/-", details: "4 Hrs - 150 km", type: "Sedan" },
  { name: "SUV 6+1", price: "₹5000/-", details: "4 Hrs - 150 km", type: "SUV" },
  { name: "MUV 7+1", price: "₹5500/-", details: "4 Hrs - 150 km", type: "MUV" },
  { name: "14 Seater", price: "₹8000/-", details: "4 Hrs - 150 km", type: "Van" },
  { name: "17 Seater", price: "₹9000/-", details: "4 Hrs - 150 km", type: "Van" },
];

const localTransport = [
  { name: "SEDAN", price: "₹3500/-", detail: "8 HOURS - 80 KM", extra: "Addl Hr: ₹350 | Km: ₹15" },
  { name: "SUV", price: "₹4200/-", detail: "8 HOURS - 80 KM", extra: "Addl Hr: ₹400 | Km: ₹18" },
  { name: "MUV", price: "₹5000/-", detail: "8 HOURS - 80 KM", extra: "Addl Hr: ₹500 | Km: ₹20" },
  { name: "14 Seater", price: "₹7700/-", detail: "8 HOURS - 80 KM", extra: "Parking & Driver Food Separate" },
  { name: "17 Seater", price: "₹8700/-", detail: "8 HOURS - 80 KM", extra: "Parking & Driver Food Separate" },
];

const outstationPackage = [
  { name: "SEDAN 4+1", price: "₹4500/-", detail: "12 HOURS - 300 KM", extra: "Addl Hr: ₹350 | Km: ₹15" },
  { name: "SUV 6+1", price: "₹6000/-", detail: "12 HOURS - 300 KM", extra: "Addl Hr: ₹400 | Km: ₹18" },
  { name: "MUV 7+1", price: "₹7000/-", detail: "12 HOURS - 300 KM", extra: "Addl Hr: ₹500 | Km: ₹20" },
  { name: "14 Seater", price: "₹7700/-", detail: "12 HOURS - 300 KM", extra: "Toll, Parking, Permit (Separate)" },
  { name: "17 Seater", price: "₹8700/-", detail: "12 HOURS - 300 KM", extra: "Toll, Parking, Permit (Separate)" },
];

const busAndTempo = [
  {
    name: "SML Mini Bus 28+1",
    type: "Bus",
    options: [
      { name: "20 Seat", price: "₹5500/-", sub: "Driver Allowance = ₹500/-" },
      { name: "28 Seat", price: "₹6500/-", sub: "Diesel Fill To Fill ✓ 12 Hours (Fees Separate)" }
    ]
  },
  {
    name: "Tempo Traveller 17+1",
    type: "Van",
    options: [
      { name: "14 Seat", price: "₹4000/-", sub: "Driver Allowance - ₹500/-" },
      { name: "17 Seat", price: "₹5000/-", sub: "Diesel Fill To Fill ✓ 12 Hours ✓ 300 Km Limit" }
    ]
  },
];

const specialTours = [
  { name: "Vellankkani (or) Tanjore (or) Vellore Golden Temple", limit: "12 Hours - 350 K.M", sedan: "₹5500/-", suv: "₹7000/-" },
  { name: "Gingee Fort", limit: "10 Hours - 150 K.M", sedan: "₹4500/-", suv: "₹5500/-" },
  { name: "Pichavaram & Chidambaram Temple", limit: "10 Hours - 180 K.M", sedan: "₹4800/-", suv: "₹6000/-" },
  { name: "Thiruvannamalai", limit: "10 Hours - 220 K.M", sedan: "₹5000/-", suv: "₹6500/-" },
  { name: "Mahabalipuram", limit: "10 Hours - 210 K.M", sedan: "₹4800/-", suv: "₹6000/-" },
];

const Pricing = () => {
  return (
    <Layout>
      <SEOHead
        title="Taxi Fare in Pondicherry | Affordable Taxi Tariff — Airport, Local & Outstation 24/7"
        description="Transparent taxi fare in Pondicherry — affordable taxi tariff for Pondicherry airport taxi service, Chennai airport drop, local sightseeing taxi, one-way drop taxi & 24/7 outstation cab Pondicherry. No hidden charges. Book now!"
        keywords="Pondicherry taxi fare, Chennai airport transfer price, Pondicherry local taxi rent, outstation taxi charges, taxi in pondicherry, best taxi services in pondicherry, taxi services in pondicherry, 24/7 taxi services, Affordable Taxi Services in Pondicherry, Affordable Taxi Fares in Pondicherry, Pondicherry local sightseeing taxi, Pondicherry city tour packages, Pondicherry sightseeing cab service, Pondicherry airport taxi service, Airport pickup and drop Pondicherry, Chennai to Pondicherry airport taxi, Pondicherry to Chennai cab, Daily pickup drop service, Office pickup drop taxi, Chennai Airport drop taxi service, Pondicherry Airport drop Taxi service, Tours and travels in pondicherry, One-way drop taxi service in pondicherry, Local Pondicherry sightseeing(City Tour), Airport transfer (Hotels)pondicherry, Outstation trips pondicherry, corporate cab service in pondicherry,taxi fare Pondicherry, taxi tariff Pondicherry, affordable taxi fares in Pondicherry, Pondicherry taxi price, hourly cab rental Pondicherry, outstation Pondicherry to Chennai cab booking, 24/7 outstation cab service Pondicherry, tempo traveller Pondicherry price, one-way drop taxi Pondicherry fare, Mahabalipuram tour taxi Pondicherry, Thiruvannamalai taxi from Pondicherry, pilgrimage tour taxi Pondicherry,Pondicherry Taxi Cost, Cab Charges in Pondicherry, Pondicherry Taxi Rate"
        canonical="https://pondicherry.taxi/pricing"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding bg-gradient-to-b from-primary/10 via-background to-transparent overflow-hidden">
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-6xl font-bold tracking-tight mb-6"
            >
              Transparent <span className="text-gradient">Taxi Fare & Tariff</span> for Every Journey in Pondicherry
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-muted-foreground"
            >
              Choose the perfect ride with our clear Pondicherry taxi fare guide — affordable taxi tariff for airport transfers, one-way drop taxi, local sightseeing taxi & 24/7 outstation cab Pondicherry. No hidden fees, just honest rates.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Airport Transfer Section */}
      <section className="py-16">
        <div className="container-custom">
          <SectionHeading
            badge="Limited Offer"
            title="Airport Taxi"
            highlight="Service Pondicherry"
            description="Pondicherry airport taxi service — Puducherry to Chennai Airport & Chennai airport drop taxi via ECR. Chennai to Pondicherry airport taxi fare included."
            align="left"
          />


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-12">
            {airportTransfer.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-6 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Plane className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-1">{item.name}</h3>
                <p className="text-2xl font-black text-primary mb-1">{item.price}</p>
                <p className="text-xs text-muted-foreground mb-6">{item.details}</p>
                <a
                  href={`https://wa.me/919952493907?text=Airport Transfer Booking: ${item.name}`}
                  className="w-full py-3 bg-white/5 border border-white/10 group-hover:bg-[#2b8a00] group-hover:text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: Parking fees & Driver food is separate
          </div>
        </div>
      </section>

      {/* Local Transport Section */}
      <section className="py-16 bg-taxi-darker/30">
        <div className="container-custom">
          <SectionHeading
            title="Local Sightseeing"
            highlight="Taxi Pondicherry"
            description="Hourly cab rental Pondicherry — local sightseeing taxi & Pondicherry city tour packages within city limits."
            align="right"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-12">
            {localTransport.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-6 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-bold mb-1">{item.name}</h3>
                <p className="text-2xl font-black text-blue-600 mb-1">{item.price}</p>
                <p className="text-sm text-muted-foreground font-bold mb-1">{item.detail}</p>
                <p className="text-[10px] text-muted-foreground mb-6">{item.extra}</p>
                <a
                  href={`https://wa.me/919952493907?text=Local Transport Booking: ${item.name}`}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-background/50 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: Parking fees and driver food is separate
          </div>
        </div>
      </section>

      {/* Outstation Section */}
      <section className="py-16">
        <div className="container-custom">
          <SectionHeading
            title="Outstation Cab"
            highlight="Pondicherry"
            description="24/7 outstation cab Pondicherry — one-way drop taxi & round trips. Pondicherry to Chennai cab, Bangalore & all South India destinations."
            align="left"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-12">
            {outstationPackage.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-6 transition-all duration-300 border-t-4 border-t-orange-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold mb-1">{item.name}</h3>
                <p className="text-2xl font-black text-orange-600 mb-1">{item.price}</p>
                <p className="text-sm text-muted-foreground font-bold mb-1">{item.detail}</p>
                <p className="text-[10px] text-muted-foreground mb-6">{item.extra}</p>
                <a
                  href={`https://wa.me/919952493907?text=Outstation Package Booking: ${item.name}`}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  Book Now
                </a>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/5 rounded-2xl border border-dashed border-white/10 text-sm text-center font-medium text-gray-400">
            Note: TOLL, PARKING, PERMIT FEES & DRIVER FOOD IS SEPARATE
          </div>
        </div>
      </section>

      {/* Bus and Tempo Section */}
      <section className="py-16 bg-gray-900 text-white rounded-[4rem] mx-4 mb-24 overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Tempo Traveller Pondicherry & <span className="text-primary">Group Travel Cab</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Comfortable tempo traveller Pondicherry & mini bus hire for group travel, pilgrimage tours, corporate cab service Pondicherry and wedding transfers.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {busAndTempo.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                      {item.type === "Bus" ? <Bus className="w-8 h-8 text-primary" /> : <Users className="w-8 h-8 text-primary" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold">{item.name}</h3>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {item.options.map((opt, oIdx) => (
                      <div key={oIdx} className="bg-background/40 p-4 rounded-2xl border border-white/5">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-bold text-white">{opt.name}</span>
                          <span className="text-xl font-black text-primary">{opt.price}</span>
                        </div>
                        <p className="text-xs text-gray-400">{opt.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold uppercase tracking-wider">AC Vehicle</span>
                  <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold uppercase tracking-wider">Pushback Seats</span>
                  <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-bold uppercase tracking-wider">Music System</span>
                </div>

                <a
                  href={`https://wa.me/919952493907?text=Traveler/Bus Booking: ${item.name}`}
                  className="mt-8 w-full py-4 bg-[#2b8a00] text-white rounded-2xl text-base font-black flex items-center justify-center gap-2 hover:bg-[#1e6000] transition-all"
                >
                  Confirm Availability
                </a>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center text-gray-500 text-sm max-w-3xl mx-auto">
            <p>Toll, Parking, Hill Charges, Permit (Separate as applicable). Diesel Fill To Fill option available for 12 Hours limit/300 Km limit as specified.</p>
          </div>
        </div>
      </section>

      {/* Special Tour Packages Section */}
      <section className="py-16">
        <div className="container-custom">
          <SectionHeading
            title="Special Tour"
            highlight="Packages from Pondicherry"
            description="All-inclusive Pondicherry city tour packages & pilgrimage taxi — Mahabalipuram, Thiruvannamalai, Vellore Golden Temple & more. Tours and travels Pondicherry at fixed fares."
            align="left"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {specialTours.map((item, idx) => (
              <motion.div
                key={idx}
                {...fadeUp}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-hover rounded-3xl p-8 border-l-4 border-l-primary"
              >
                <h3 className="text-xl font-extrabold mb-2">{item.name}</h3>
                {item.limit && <p className="text-sm text-muted-foreground mb-4 font-bold">{item.limit}</p>}

                <div className="space-y-4 mt-6">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-xl">
                    <span className="text-sm font-medium">Sedan (4 Seater)</span>
                    <span className="text-lg font-black text-primary">{item.sedan}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-xl">
                    <span className="text-sm font-medium">SUV (6 Seater)</span>
                    <span className="text-lg font-black text-primary">{item.suv}</span>
                  </div>
                </div>

                <p className="mt-6 text-[10px] text-center text-gray-500 font-medium">Driver Food, Toll & Parking fees is separate</p>

                <a
                  href={`https://wa.me/919952493907?text=Special Package Booking: ${item.name}`}
                  className="mt-6 w-full py-3 bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all"
                >
                  Book This Package
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Note Section (Simplified) */}
      <section className="pb-24">
        <div className="container-custom max-w-4xl pt-10">
          <motion.div
            {...fadeUp}
            className="bg-taxi-darker border border-white/10 rounded-[2.5rem] p-8 sm:p-12 shadow-2xl relative overflow-hidden text-center"
          >
            <h2 className="text-2xl font-bold mb-6">Need a <span className="text-primary">Custom Taxi Fare Quote?</span></h2>
            <p className="text-muted-foreground mb-10">Looking for a custom cab booking Pondicherry rate? Group travel, corporate cab service Pondicherry, or outstation Pondicherry to Chennai cab booking — we offer packages tailored to your needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+919952493907" className="bg-[#2b8a00] hover:bg-[#1e6000] text-white inline-flex items-center gap-2 py-4 px-8 rounded-2xl font-bold shadow-lg transition-colors">
                <Phone className="w-5 h-5" /> Call Now
              </a>
              <a href="https://wa.me/919952493907" className="inline-flex items-center gap-2 py-4 px-8 rounded-2xl font-bold border-2 border-[#2b8a00] text-[#2b8a00] hover:bg-[#2b8a00]/5 transition-colors">
                <Phone className="w-5 h-5 opacity-0 absolute" /> {/* Hidden icon for space */}
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Pricing;
